// packages/web/src/screens/MaterialsReelsAssist.tsx — B11: Materials -> Reels
// on the actor_assist pack. Named `…Assist`, deliberately, so it is never
// confused with the retired Actor pack's own `MaterialsReels.tsx` (reached
// only from `MATERIALS_SECTIONS`, over the `actor_media` type this pack
// never seeds — see YourPage.tsx's own header for the full account of that
// door's failure mode).
//
// Why this is its OWN screen and not `MaterialsPhotos.tsx` widened a third
// way (the brief's own fork in the road, §1): a reel needs a video `accept`,
// an upload-progress readout (a reel can take minutes on LTE — "Adding…"
// alone reads as broken), and a COVER STILL instead of an `<img>` of the
// video itself, chosen by the artist scrubbing their own upload (P2). That
// is the extraction hook's OWN UI (the scrub bar, the re-extract-on-drag),
// which does not exist on `MaterialsPhotos.tsx` at all — bending that
// screen a third way would have meant a `kind === 'reel'` branch inside
// nearly every piece of it (accept, grid card, upload act, progress). More
// than "a few branches", so per the brief this is its own screen, borrowing
// the shape (`materials__scroll` / `pgrid` / `lookcard` / `addmedia`, all
// from Materials.css — the same classes MaterialsPhotos.tsx already uses).
//
// The same door MaterialsPhotos.tsx uses for its OWN read
// (`listAllRecordsByType` + `activeOnly` + a `kind` filter) — never
// `listMedia`/`/api/actor/media`, which serves the retired Actor pack's
// `actor_media` type and 404s "unknown record type" on this deployment
// (`recordFilesRoute.test.ts`'s own header).
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  ApiError,
  listAllRecordsByType,
  updateRecord,
  uploadPhoto,
  type RecordRow,
} from '../lib/api';
import { activeOnly } from '../lib/retired';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { openRecordEditor } from '../lib/recordEditor';
import { useCoverFrameCapture } from '../lib/coverFrameCapture';
import { RecordImage } from '../components/RecordMedia';
import { AA } from '../lib/auditionSpread';
import './Materials.css';

export interface MaterialsReelsAssistProps {
  tenantId: string;
}

const WORDS = {
  group: 'Reels',
  empty: 'No reels yet.',
  add: '+ Add reel',
  aria: 'Add reel file',
  failed: 'The reel did not upload.',
} as const;

export function MaterialsReelsAssist({ tenantId }: MaterialsReelsAssistProps) {
  const [reels, setReels] = useState<RecordRow[] | null>(null);
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [subjectId, setSubjectId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  // ⚑ THE MEDIA ID THE NEXT CAPTURE WRITES TO. `capture()` fires only AFTER
  // `uploadPhoto` has already created the `aa_media` row (§2 — the cover is
  // captured "right after the upload succeeds"), so the hook's own
  // `onCaptured` closes over this ref rather than a plain variable — see
  // coverFrameCapture.ts's own header for why that matters for a rescrub.
  const activeMediaIdRef = useRef<string | null>(null);
  const { state: cover, capture: captureCover, rescrub } = useCoverFrameCapture(tenantId, (coverRef) => {
    const mediaId = activeMediaIdRef.current;
    if (!mediaId) return;
    // P2's own rule, applied here too: extraction is a best-effort second
    // act on an upload that already succeeded. A failed WRITE of the
    // already-captured still is not treated as a fresh extraction failure —
    // it is rare enough (the record was just created) that surfacing it as
    // the extraction's own loud sentence would say the wrong thing.
    // The grid re-reads once the still is ON the record — otherwise the tile
    // keeps its plate until the next visit (walk, 2026-09-25). A failed
    // write is said, never swallowed: the still was captured but is not on
    // the reel, and the page will show the plate until it is.
    void updateRecord(tenantId, mediaId, { cover_frame_ref: coverRef })
      .then(() => setNonce((n) => n + 1))
      .catch((err: unknown) => {
        setError(
          err instanceof ApiError
            ? (err.serverMessage ?? err.message)
            : 'The cover did not save onto the reel. Move the slider to choose it again.'
        );
      });
  });

  useEffect(() => {
    let cancelled = false;
    void Promise.all([listAllRecordsByType(tenantId, AA.MEDIA), listAllRecordsByType(tenantId, AA.SUBJECT)])
      .then(([media, subjects]) => {
        if (cancelled) return;
        setReels(activeOnly(media).filter((r) => r.fields['kind'] === 'reel'));
        // One account, at most one subject now (R5) — the same lookup
        // MaterialsPhotos.tsx's own upload door uses.
        setSubjectId(subjects[0]?.id ?? null);
        setFailure(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setReels([]);
        setFailure(describeReadFailure(err));
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, nonce]);

  async function onFile(e: Event) {
    const input = e.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (!subjectId) {
      // Unlike MaterialsPhotos.tsx's own photo door, a reel with nobody to
      // be OF is not filed anyway: `media_subject` is the pack's own read
      // scope for `aa_media` (uploadHeadshot's comment explains it), and a
      // household that has never claimed its own career has no performer
      // yet to lead a page with a reel at all.
      setError('Set up a performer before adding a reel.');
      return;
    }
    setUploading(true);
    setProgress(0);
    setError(null);
    try {
      // §1 — bytes go up first (blob -> record -> link, `uploadPhoto`'s own
      // order), reporting progress on the presigned PUT's own bytes moving.
      const { mediaId } = await uploadPhoto(tenantId, subjectId, file, { kind: 'reel', onProgress: setProgress });
      setNonce((n) => n + 1);
      setUploading(false);
      setProgress(null);
      // §2 — THE COVER, RIGHT AFTER THE UPLOAD SUCCEEDS. The reel is IN
      // either way; a failed extraction is loud (`cover.extractError`,
      // rendered per-card below) and the reel itself stands — P2's own
      // rule, never a reason to fail what already succeeded.
      activeMediaIdRef.current = mediaId;
      await captureCover(file, undefined);
    } catch (err) {
      // ⚑ STORAGE REFUSAL IS LOUD. The server's own sentence — never
      // "something went wrong" — same pattern MaterialsPhotos.tsx's own
      // upload door follows.
      setError(err instanceof ApiError ? (err.serverMessage ?? err.message) : WORDS.failed);
      setUploading(false);
      setProgress(null);
    } finally {
      input.value = '';
    }
  }

  const addLabel = uploading
    ? progress !== null
      ? `Uploading ${Math.round(progress * 100)}%`
      : 'Adding…'
    : WORDS.add;

  return (
    <div class="materials__scroll">
      <div class="materials__grp">{WORDS.group}</div>
      {reels === null ? (
        <div class="materials__empty">One moment…</div>
      ) : failure ? (
        <div class="materials__error">
          {failure.headline} {failure.detail}
        </div>
      ) : reels.length === 0 ? (
        <div class="materials__empty">{WORDS.empty}</div>
      ) : (
        <div class="pgrid">
          {reels.map((m) => {
            const coverRef = m.fields['cover_frame_ref'];
            const hasCover = typeof coverRef === 'string' && coverRef.length > 0;
            return (
              <div key={m.id} class="lookcard">
                <button
                  type="button"
                  class="lookcard__art"
                  onClick={() => openRecordEditor({ mode: 'edit', typeKey: AA.MEDIA, id: m.id })}
                >
                  {hasCover ? (
                    <RecordImage
                      tenantId={tenantId}
                      recordId={m.id}
                      fieldName="cover_frame_ref"
                      alt={reelLabel(m)}
                      className="lookcard__img"
                    />
                  ) : (
                    // P2's own rule, extended here: never a black rectangle
                    // (frame 0 of most reels), and never a broken player
                    // inside a grid card — a plain typographic plate.
                    <div class="lookcard__plate" aria-hidden="true">
                      {reelLabel(m)}
                    </div>
                  )}
                </button>
                <div class="lookcard__meta">
                  <div class="lookcard__t">{reelLabel(m)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div class="addmedia">
        <button type="button" class="addmedia__btn" disabled={uploading} onClick={() => fileRef.current?.click()}>
          {addLabel}
        </button>
        <input
          ref={fileRef}
          class="addmedia__input"
          type="file"
          accept="video/*"
          aria-label={WORDS.aria}
          onChange={onFile}
        />
      </div>
      {error ? <div class="materials__error" data-testid="reels-error">{error}</div> : null}
      {/* §2 — the still, with a scrubber to pick a different moment. Shown
          for whichever reel was JUST uploaded (`activeMediaIdRef`), the
          same `captureAt`/`rescrub` shape RecordForm.tsx's own cover-frame
          picker uses (now the shared hook both screens call). */}
      {cover.extracting || cover.previewUrl || cover.extractError ? (
        <div class="materials__coverpick" data-testid="reels-cover-frame">
          {cover.extracting && <div class="materials__empty">Choosing a cover frame…</div>}
          {cover.extractError && (
            <div class="materials__error" data-testid="reels-cover-error">
              {cover.extractError} Open the reel and attach a still yourself in its Cover frame field.
            </div>
          )}
          {cover.previewUrl && (
            <>
              <img src={cover.previewUrl} alt="Chosen cover frame" class="materials__coverimg" />
              {cover.durationSeconds !== null && cover.durationSeconds > 0 && (
                <input
                  type="range"
                  class="materials__coverscrub"
                  data-testid="reels-cover-scrub"
                  min={0}
                  max={cover.durationSeconds}
                  step={0.1}
                  value={cover.seekSeconds}
                  disabled={cover.extracting}
                  onChange={(e) => void rescrub(Number((e.target as HTMLInputElement).value))}
                />
              )}
              <div class="materials__empty">This is the still shown before anyone taps play.</div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

function reelLabel(m: RecordRow): string {
  const label = m.fields['label'];
  return typeof label === 'string' && label.trim() ? label : 'Reel';
}
